import { useMemo, useState, type ChangeEvent } from "react";

export type Filters = {
  board: string; // MOEX-борт: TQCB — корпораты, TQOB — ОФЗ/субфеды
  currency: string;
  target: number; // в десятичных, 0.16 == 16%
  tax: string;
  maxMonths: number | null;
  favoritesOnly: boolean;
  hideUnavailable: boolean;
  search: string;
};

// Сегмент выбора рынка: три валютных таба сидят на одном борту корпоратов
// (TQCB), ОФЗ — отдельный борт TQOB, всегда в рублях. Таб определяется парой
// board+currency, потому что RUB-корпораты и ОФЗ обе рублёвые, но на разных
// бортах.
const TABS = [
  { key: "RUB", label: "RUB", board: "TQCB", currency: "RUB" },
  { key: "USD", label: "USD", board: "TQCB", currency: "USD" },
  { key: "CNY", label: "CNY", board: "TQCB", currency: "CNY" },
  { key: "OFZ", label: "ОФЗ", board: "TQOB", currency: "RUB" },
];
const TAX_OPTIONS = [
  { value: "resident", label: "резидент 13%" },
  { value: "non-resident", label: "нерезидент 30%" },
  { value: "iis-b", label: "ИИС-Б 0%" },
];

// Все фильтры собраны в один контейнер .filter-bar-grid. На десктопе это
// inline-flex с переносами (узкие лейблы перед инпутами); на мобилке —
// 2-колоночный grid тайлов с лейблом сверху и значением снизу. Лейблы
// «налог»/«поиск» на десктопе не показываем (.ff-l-mob-only), потому что
// там селект/поиск визуально самодостаточны.
//
// .ff-collapse-bar — сворачивающая кнопка для мобилки. На десктопе скрыта.
// Когда collapsed, на мобилке скрываются все элементы с .is-collapsible
// (тайлы фильтров + ★/🔒 пиллы); валютный сегмент и сама шапка остаются.
export function FilterBar({
  filters,
  onChange,
  favCount,
  hiddenCount,
}: {
  filters: Filters;
  onChange: (next: Partial<Filters>) => void;
  favCount: number;
  hiddenCount: number;
}) {
  const [open, setOpen] = useState(true);

  const num = (e: ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value);
    return Number.isFinite(v) ? v : null;
  };

  // Краткая сводка активных фильтров для свёрнутого состояния. Считаем как
  // «значимые ограничения» — target всегда показываем (он базовый порог),
  // остальное — только если пользователь явно выставил.
  const summary = useMemo(() => {
    const parts: string[] = [];
    parts.push(`≥${(filters.target * 100).toFixed(0)}%`);
    if (filters.maxMonths != null) parts.push(`≤${filters.maxMonths} мес`);
    const tax = TAX_OPTIONS.find((o) => o.value === filters.tax)?.label;
    if (tax) {
      const m = tax.match(/(\d+)\s*%/);
      if (m) parts.push(`${m[1]}%`);
    }
    if (filters.favoritesOnly) parts.push("★");
    if (filters.search.trim()) parts.push(`"${filters.search.trim()}"`);
    return parts;
  }, [filters]);

  return (
    <div className="filter-bar">
      <div className={`filter-bar-grid${open ? "" : " is-collapsed"}`}>
        <div className="ff-seg">
          <div className="seg">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                className={
                  filters.board === t.board && filters.currency === t.currency
                    ? "on"
                    : ""
                }
                onClick={() => onChange({ board: t.board, currency: t.currency })}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          className="ff-collapse-bar"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
        >
          <span className="ff-collapse-left">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M2 3h10M4 7h6M6 11h2"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
            </svg>
            <span>Фильтры</span>
            {!open && (
              <span className="ff-collapse-summary mono">
                · {summary.join(" · ")}
              </span>
            )}
            <span className="ff-collapse-badge mono">{summary.length}</span>
          </span>
          <span className="ff-collapse-right">
            <span className="ff-collapse-state">
              {open ? "свернуть" : "развернуть"}
            </span>
            <svg
              className={`ff-collapse-chevron${open ? " is-open" : ""}`}
              width="11"
              height="11"
              viewBox="0 0 11 11"
              fill="none"
            >
              <path
                d="M2 4l3.5 3.5L9 4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>

        <label className="ff is-collapsible">
          <span className="ff-l">цель ≥</span>
          <input
            className="input ff-num"
            type="number"
            step={0.5}
            min={0}
            value={(filters.target * 100).toFixed(1)}
            onChange={(e) => {
              const v = num(e);
              if (v != null) onChange({ target: v / 100 });
            }}
          />
          <span className="ff-suffix">%</span>
        </label>

        <label className="ff is-collapsible">
          <span className="ff-l">срок ≤</span>
          <input
            className="input ff-num"
            type="number"
            min={0}
            step={1}
            placeholder="мес"
            value={filters.maxMonths ?? ""}
            onChange={(e) =>
              onChange({ maxMonths: e.target.value === "" ? null : num(e) })
            }
          />
        </label>

        <label className="ff ff-tax is-collapsible">
          <span className="ff-l ff-l-mob-only">налог</span>
          <select
            className="input"
            value={filters.tax}
            onChange={(e) => onChange({ tax: e.target.value })}
          >
            {TAX_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>

        <label className="ff ff-search is-collapsible">
          <span className="ff-l ff-l-mob-only">поиск</span>
          <input
            className="input"
            type="search"
            placeholder="поиск…"
            value={filters.search}
            onChange={(e) => onChange({ search: e.target.value })}
          />
        </label>

        <button
          type="button"
          className={`pill ff-pill is-collapsible${filters.favoritesOnly ? " on" : ""}`}
          onClick={() => onChange({ favoritesOnly: !filters.favoritesOnly })}
        >
          ★ избранное <span style={{ opacity: 0.6 }}>({favCount})</span>
        </button>

        <button
          type="button"
          className={`pill ff-pill is-collapsible${filters.hideUnavailable ? " on" : ""}`}
          onClick={() =>
            onChange({ hideUnavailable: !filters.hideUnavailable })
          }
          title="скрыть бумаги, помеченные как недоступные"
        >
          🔒 скрыть <span style={{ opacity: 0.6 }}>({hiddenCount})</span>
        </button>
      </div>
    </div>
  );
}
